import React, { useMemo } from "react";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import {
  FiAlertCircle,
  FiArrowLeft,
  FiArrowRight,
  FiPlusCircle,
} from "react-icons/fi";
import Logo from "../../assets/images/LogoNew.svg";
import {
  buildLoginV2Decision,
  doesCreateOptionMatchCurrentRoute,
  getApplicationOptionLogicalKey,
  getCreateOptionLogicalKey,
  normalizeApplicationOption,
  normalizeCreateOption,
} from "./loginV2Decision";
import {
  getApplicationStatusLabel,
  getFormTypeLabel,
} from "../../util/applicationLabels";
import { normalizeAutomationTestId } from "../../components/InputField/testId";
import "./WorkableApplicationsScreen.scss";

const getApplicationStableReference = (application = {}) => (
  application.applicationNumber ||
  application.applicationNo ||
  application.applicationReference ||
  application.referenceNumber ||
  application.businessApplicationNumber ||
  application.customerApplicationNumber ||
  ""
);

const getApplicationCustomerName = (application = {}) => (
  typeof application.customerName === "string" ? application.customerName.trim() : ""
);

const getApplicationFormEmail = (application = {}) => (
  typeof application.applicationFormEmail === "string" ? application.applicationFormEmail.trim() : ""
);

const normalizeMessage = (message = "") => String(message || "")
  .trim()
  .replace(/\s+/g, " ")
  .toLowerCase();

const BLOCKED_REASON_KIND = {
  DUPLICATE: "duplicate",
  PREPARING: "preparing",
  COMPLETE: "complete",
  ACTIVE_SESSION: "active-session",
  REJECTED: "rejected",
  UNAUTHORIZED: "unauthorized",
  ROUTE_MISMATCH: "route-mismatch",
  SESSION_EXPIRED: "session-expired",
  NO_APPLICATIONS: "no-applications",
  GENERIC: "generic",
};

const getBlockedReasonKind = (reason = "") => {
  const rawReason = String(reason || "").trim();

  if (/already present/i.test(rawReason)) {
    return BLOCKED_REASON_KIND.DUPLICATE;
  }

  if (/account is being created/i.test(rawReason)) {
    return BLOCKED_REASON_KIND.PREPARING;
  }

  if (/account is created/i.test(rawReason)) {
    return BLOCKED_REASON_KIND.COMPLETE;
  }

  if (/actively working/i.test(rawReason)) {
    return BLOCKED_REASON_KIND.ACTIVE_SESSION;
  }

  if (/rejected/i.test(rawReason)) {
    return BLOCKED_REASON_KIND.REJECTED;
  }

  if (/unauthorized access/i.test(rawReason)) {
    return BLOCKED_REASON_KIND.UNAUTHORIZED;
  }

  if (/login page cannot start|different application type|matching login page/i.test(rawReason)) {
    return BLOCKED_REASON_KIND.ROUTE_MISMATCH;
  }

  if (/secure login session has expired/i.test(rawReason)) {
    return BLOCKED_REASON_KIND.SESSION_EXPIRED;
  }

  if (/no applications are available/i.test(rawReason)) {
    return BLOCKED_REASON_KIND.NO_APPLICATIONS;
  }

  return BLOCKED_REASON_KIND.GENERIC;
};

const getHumanReadableBlockedReason = (reason = "") => {
  switch (getBlockedReasonKind(reason)) {
    case BLOCKED_REASON_KIND.DUPLICATE:
      return "This application cannot be continued right now. Please continue an available application from this list.";
    case BLOCKED_REASON_KIND.PREPARING:
      return "This application is not ready yet. Please try again later.";
    case BLOCKED_REASON_KIND.COMPLETE:
      return "This application is already complete.";
    case BLOCKED_REASON_KIND.ACTIVE_SESSION:
      return "This application is open in another session. Please try again later.";
    case BLOCKED_REASON_KIND.REJECTED:
      return "This application cannot be continued.";
    case BLOCKED_REASON_KIND.UNAUTHORIZED:
      return "We could not open this application. Please choose another available option or sign in again.";
    case BLOCKED_REASON_KIND.ROUTE_MISMATCH:
      return "This email is linked to a different application type. Please use the matching login page.";
    case BLOCKED_REASON_KIND.SESSION_EXPIRED:
      return "Your secure login session has expired. Please sign in again.";
    case BLOCKED_REASON_KIND.NO_APPLICATIONS:
      return "No applications are available for this login.";
    case BLOCKED_REASON_KIND.GENERIC:
    default:
      return "This application cannot be continued right now.";
  }
};

const getHumanReadableCreateBlockedReason = (reason = "") => {
  switch (getBlockedReasonKind(reason)) {
    case BLOCKED_REASON_KIND.DUPLICATE:
      return "Starting a new application is not available for this account right now.";
    case BLOCKED_REASON_KIND.PREPARING:
      return "This application is not ready yet. Please try again later.";
    case BLOCKED_REASON_KIND.COMPLETE:
      return "This application is already complete.";
    case BLOCKED_REASON_KIND.ACTIVE_SESSION:
      return "This application is open in another session. Please try again later.";
    case BLOCKED_REASON_KIND.UNAUTHORIZED:
      return "We could not start this application. Please sign in again.";
    case BLOCKED_REASON_KIND.ROUTE_MISMATCH:
      return "This email is linked to a different application type. Please use the matching login page.";
    case BLOCKED_REASON_KIND.SESSION_EXPIRED:
      return "Your secure login session has expired. Please sign in again.";
    case BLOCKED_REASON_KIND.NO_APPLICATIONS:
      return "No applications are available for this login.";
    case BLOCKED_REASON_KIND.REJECTED:
    case BLOCKED_REASON_KIND.GENERIC:
    default:
      return "Starting a new application is not available right now.";
  }
};

const getHumanReadablePageError = (error = "") => {
  const rawError = String(error || "").trim();
  return rawError ? getHumanReadableBlockedReason(rawError) : "";
};

const getApplicationTestKey = (application = {}) => {
  const normalizedApplication = normalizeApplicationOption(application);
  const baseKey = getApplicationOptionLogicalKey(normalizedApplication);
  const stableReference = getApplicationStableReference(application);
  return stableReference ? `${baseKey}-${normalizeAutomationTestId(stableReference)}` : baseKey;
};

const getCreateTestKey = (createOption = {}) => (
  getCreateOptionLogicalKey(normalizeCreateOption(createOption))
);

const uniqueBy = (items, keyGetter) => {
  const seen = new Set();
  return items.filter((item) => {
    const key = keyGetter(item);
    if (seen.has(key)) {
      return false;
    }
    seen.add(key);
    return true;
  });
};

const buildApplicationItems = ({ decision, applications }) => {
  const source = applications?.length
    ? applications
    : decision?.applications || [
      ...(decision?.resumableApplications || []),
      ...(decision?.blockedApplications || []),
    ];

  return uniqueBy(source.map(normalizeApplicationOption), (item) => (
    `${item.applicationId}:${getApplicationTestKey(item)}`
  ));
};

const buildCreateItems = ({ decision, createOptions, form }) => {
  const source = decision?.allowedCreateOptions?.length
    ? decision.allowedCreateOptions
    : createOptions;

  const normalizedItems = source.map(normalizeCreateOption).filter((item) => item.allowed);
  const filteredItems = form
    ? normalizedItems.filter((item) => doesCreateOptionMatchCurrentRoute(item, form))
    : normalizedItems;

  return uniqueBy(filteredItems, getCreateTestKey);
};

const LoadingState = () => (
  <div
    className="auth-workable__list"
    data-testid="auth-workable-applications-loading"
  >
    {[0, 1].map((item) => (
      <div className="auth-workable__card" key={item}>
        <Skeleton height={28} width="55%" />
        <Skeleton height={16} width="70%" />
        <Skeleton height={42} />
      </div>
    ))}
  </div>
);

const EmptyState = ({ message }) => (
  <div className="auth-workable__empty" data-testid="auth-workable-applications-empty">
    <FiAlertCircle aria-hidden="true" />
    <span>{message}</span>
  </div>
);

const ApplicationCard = ({
  application,
  actionLoadingKey,
  onResume,
}) => {
  const testKey = getApplicationTestKey(application);
  const recordId = application.applicationId;
  const title = getFormTypeLabel(
    application.applicationFormType || application.formType,
    application.type
  );
  const customerName = getApplicationCustomerName(application);
  const applicationFormEmail = getApplicationFormEmail(application);
  const primaryTitle = customerName || applicationFormEmail || title;
  const secondaryTitle = customerName || applicationFormEmail ? title : "";
  const statusText = getApplicationStatusLabel(application.status);
  const stableReference = getApplicationStableReference(application);
  const disabled = !application.resumeAllowed;
  const isLoading = actionLoadingKey === `resume:${testKey}`;
  const formattedBlockedReason = getHumanReadableBlockedReason(application.blockedReason);

  return (
    <article
      className={`auth-workable__card ${disabled ? "auth-workable__card--blocked" : ""}`}
      data-testid={`auth-workable-application-card-${testKey}`}
      data-record-id={recordId || undefined}
    >
      <div className="auth-workable__card-main">
        <div className="auth-workable__content">
          <div className="auth-workable__card-labels">
            <span
              className="auth-workable__type-label"
              data-testid={`auth-workable-application-form-type-${testKey}`}
            >
              {secondaryTitle || title}
            </span>
            {stableReference ? (
              <span
                className="auth-workable__reference"
                data-testid={`auth-workable-application-reference-${testKey}`}
              >
                App #{stableReference}
              </span>
            ) : null}
          </div>
          <h4 data-testid={`auth-workable-application-title-${testKey}`}>
            {primaryTitle}
          </h4>
          <div
            className="auth-workable__status-line"
            data-testid={`auth-workable-application-status-${testKey}`}
          >
            <span>Status:</span>{" "}
            <strong>{statusText}</strong>
          </div>
          {application.blockedReason ? (
            <div
              className="auth-workable__blocked-reason"
              data-testid={`auth-workable-application-blocked-${testKey}`}
            >
              {formattedBlockedReason}
            </div>
          ) : null}
        </div>
      </div>
      {!disabled ? (
        <button
          type="button"
          className="auth-workable__action"
          data-testid={`auth-workable-application-resume-${testKey}`}
          data-record-id={recordId || undefined}
          disabled={isLoading}
          onClick={() => onResume?.(application)}
        >
          {isLoading ? "Opening..." : "Continue"}
          <FiArrowRight aria-hidden="true" />
        </button>
      ) : null}
    </article>
  );
};

const CreateOptionCard = ({
  createOption,
  actionLoadingKey,
  onCreate,
}) => {
  const testKey = getCreateTestKey(createOption);
  const title = getFormTypeLabel(createOption.formType, createOption.type);
  const disabled = !createOption.allowed;
  const isLoading = actionLoadingKey === `create:${testKey}`;
  const formattedBlockedReason = getHumanReadableCreateBlockedReason(createOption.blockedReason);

  return (
    <article
      className={`auth-workable__card ${disabled ? "auth-workable__card--blocked" : ""}`}
      data-testid={`auth-create-option-${testKey}`}
    >
      <div className="auth-workable__card-main">
        <div className="auth-workable__icon auth-workable__icon--create">
          <FiPlusCircle aria-hidden="true" />
        </div>
        <div className="auth-workable__content">
          <div className="auth-workable__eyebrow">Start new application</div>
          <h4>{title}</h4>
          {createOption.blockedReason ? (
            <div
              className="auth-workable__blocked-reason"
              data-testid={`auth-create-option-blocked-${testKey}`}
            >
              {formattedBlockedReason}
            </div>
          ) : null}
        </div>
      </div>
      <button
        type="button"
        className="auth-workable__action"
        data-testid={`auth-create-option-start-${testKey}`}
        disabled={disabled || isLoading}
        onClick={() => onCreate?.(createOption)}
      >
        {isLoading ? "Starting..." : "Start"}
        <FiArrowRight aria-hidden="true" />
      </button>
    </article>
  );
};

const WorkableApplicationsScreen = ({
  decision,
  applications = [],
  createOptions = [],
  form = null,
  loading = false,
  actionLoadingKey = "",
  error = "",
  email = "",
  onResume,
  onCreate,
  onBack,
}) => {
  const effectiveDecision = useMemo(() => (
    decision || buildLoginV2Decision({ applications, createOptions, form: form || {} })
  ), [applications, createOptions, decision, form]);

  const applicationItems = useMemo(() => (
    buildApplicationItems({ decision: effectiveDecision, applications })
  ), [applications, effectiveDecision]);

  const createItems = useMemo(() => (
    buildCreateItems({ decision: effectiveDecision, createOptions, form })
  ), [createOptions, effectiveDecision, form]);

  const hasItems = applicationItems.length > 0 || createItems.length > 0;
  const hasActionableItems = applicationItems.some((application) => application.resumeAllowed) || createItems.length > 0;
  const contextualBlockedReasons = [
    ...applicationItems.map((application) => ({
      rawReason: application.blockedReason,
      displayReason: getHumanReadableBlockedReason(application.blockedReason),
    })),
    ...createItems.map((createOption) => ({
      rawReason: createOption.blockedReason,
      displayReason: getHumanReadableCreateBlockedReason(createOption.blockedReason),
    })),
  ].filter(({ rawReason }) => Boolean(rawReason));
  const hasDuplicateContextualError = Boolean(error) && contextualBlockedReasons.some((reason) => (
    normalizeMessage(reason.rawReason) === normalizeMessage(error) ||
    normalizeMessage(reason.displayReason) === normalizeMessage(error)
  ));
  const shouldShowPageError = Boolean(error) && !hasDuplicateContextualError;
  const pageErrorMessage = getHumanReadablePageError(error);
  const headerTitle = hasActionableItems ? "Choose how to continue" : "Your applications";
  const headerDescription = email
    ? `Signed in as ${email}`
    : hasActionableItems
      ? "Continue an existing application or start a new one."
      : "Review the applications available for this login.";

  return (
    <div
      className="form-content-container auth-workable"
      data-testid="auth-workable-applications"
    >
      <div className="form-content-header auth-workable__topbar">
        <img
          src={Logo}
          alt="fv-bank"
          className="logo logo-dark form-content-logo"
        />
      </div>

      <div className="form-content-body with-shadow auth-workable__shell">
        <div className="form-content-body-header auth-workable__title">
          {headerTitle}
        </div>
        <div className="form-content-body-content auth-workable__body-content">
          <div className="auth-workable__intro">
            <p data-testid="auth-workable-applications-signed-in">{headerDescription}</p>
          </div>

          {shouldShowPageError ? (
            <div className="auth-workable__error" data-testid="auth-workable-applications-error">
              <FiAlertCircle aria-hidden="true" />
              <span>{pageErrorMessage}</span>
            </div>
          ) : null}

          {loading ? (
            <LoadingState />
          ) : (
            <>
              {hasItems ? (
                <div
                  className="auth-workable__list"
                  data-testid="auth-workable-applications-list"
                >
                  {applicationItems.map((application) => (
                    <ApplicationCard
                      key={`${application.applicationId}:${getApplicationTestKey(application)}`}
                      application={application}
                      actionLoadingKey={actionLoadingKey}
                      onResume={onResume}
                    />
                  ))}
                  {createItems.map((createOption) => (
                    <CreateOptionCard
                      key={getCreateTestKey(createOption)}
                      createOption={createOption}
                      actionLoadingKey={actionLoadingKey}
                      onCreate={onCreate}
                    />
                  ))}
                </div>
              ) : (
                <EmptyState message="No applications are available for this login." />
              )}
            </>
          )}

          {onBack ? (
            <div className="auth-workable__footer">
              <button
                type="button"
                className="auth-workable__back"
                data-testid="auth-workable-applications-back"
                onClick={onBack}
              >
                <FiArrowLeft aria-hidden="true" />
                Sign in with another email
              </button>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export {
  getApplicationStableReference,
  getApplicationTestKey,
  getCreateTestKey,
  getHumanReadableBlockedReason,
  getHumanReadableCreateBlockedReason,
  getHumanReadablePageError,
};

export default WorkableApplicationsScreen;
